import React from 'react';
import { User, Bot, Sparkles, AlertCircle, CheckCircle2 } from 'lucide-react';
import StructuredResponse from './StructuredResponse';

/**
 * ChatMessage - Individual message in the chat
 * Supports: user, assistant, system message types
 */
const ChatMessage = ({
  message,
  onAction,
  disabled = false,
  taskStatus = 'planning'
}) => {
  const isUser = message.role === 'user';
  const isSystem = message.role === 'system';
  const isAssistant = message.role === 'assistant';

  // System message (status updates, notifications)
  if (isSystem) {
    return (
      <div className="flex justify-center my-4">
        <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-slate-800/50 border border-slate-700/50">
          {message.type === 'success' && <CheckCircle2 className="w-4 h-4 text-emerald-400" />}
          {message.type === 'error' && <AlertCircle className="w-4 h-4 text-red-400" />}
          {message.type === 'info' && <Sparkles className="w-4 h-4 text-blue-400" />}
          <span className="text-xs text-slate-400">{message.content}</span>
          <span className="text-xs text-slate-600">
            {message.timestamp && new Date(message.timestamp).toLocaleTimeString()}
          </span>
        </div>
      </div>
    );
  }

  // User message
  if (isUser) {
    return (
      <div className="flex justify-end mb-6">
        <div className="flex items-start gap-3 max-w-[80%]">
          <div className="flex-1">
            <div className="bg-emerald-600/20 border border-emerald-500/30 rounded-2xl rounded-tr-sm p-4">
              <p className="text-emerald-100 whitespace-pre-wrap">{message.content}</p>
              
              {/* Attachments */}
              {message.attachments && message.attachments.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {message.attachments.map((attachment, idx) => (
                    <div key={idx} className="rounded-lg overflow-hidden">
                      {attachment.type?.startsWith('image/') ? (
                        <img
                          src={attachment.preview || attachment.url}
                          alt={attachment.name}
                          className="max-w-[200px] max-h-[150px] object-cover"
                        />
                      ) : (
                        <div className="flex items-center gap-2 bg-slate-800/50 px-3 py-2 text-xs">
                          <span className="text-slate-400">{attachment.name}</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="flex justify-end mt-1">
              <span className="text-xs text-slate-500">
                {message.timestamp && new Date(message.timestamp).toLocaleTimeString()}
              </span>
            </div>
          </div>
          <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
            <User className="w-4 h-4 text-emerald-400" />
          </div>
        </div>
      </div>
    );
  }

  // Assistant message
  if (isAssistant) {
    return (
      <div className="flex justify-start mb-6">
        <div className="flex items-start gap-3 max-w-[85%]">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center flex-shrink-0 shadow-lg shadow-purple-500/20">
            <Bot className="w-4 h-4 text-white" />
          </div>
          <div className="flex-1">
            <div className="bg-slate-800/50 border border-slate-700/50 rounded-2xl rounded-tl-sm p-4">
              {/* Plain text content */}
              {message.content && typeof message.content === 'string' && (
                <p className="text-emerald-100 whitespace-pre-wrap mb-3">{message.content}</p>
              )}
              
              {/* Structured response */}
              {message.structured && (
                <StructuredResponse
                  response={message.structured}
                  onAction={onAction}
                  disabled={disabled}
                  taskStatus={taskStatus}
                />
              )}

              {/* Loading state */}
              {message.isLoading && (
                <div className="flex items-center gap-2">
                  <div className="flex gap-1">
                    <div className="w-2 h-2 bg-emerald-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <div className="w-2 h-2 bg-emerald-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <div className="w-2 h-2 bg-emerald-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                  <span className="text-sm text-slate-400">Analyzing...</span>
                </div>
              )}
            </div>
            <div className="flex justify-start mt-1">
              <span className="text-xs text-slate-500">
                {message.timestamp && new Date(message.timestamp).toLocaleTimeString()}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default ChatMessage;
